import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteContentContext = createContext();

// Helper to detect language from URL path
const getLanguageFromPath = (pathname) => {
  // If path starts with /en, it's English
  if (pathname.startsWith('/en') || pathname === '/en') {
    return 'en';
  }
  // Otherwise, it's Bulgarian (default)
  return 'bg';
};

// Helper to get path without language prefix
const getPathWithoutLang = (pathname) => {
  if (pathname.startsWith('/en/')) {
    return pathname.slice(3); // Remove '/en' prefix
  }
  if (pathname === '/en') {
    return '/';
  }
  return pathname;
};

// Helper to add language prefix to path
const getLocalizedPath = (path, lang) => {
  if (lang === 'en') {
    if (path === '/') return '/en';
    return `/en${path}`;
  }
  return path;
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get language from URL
  const language = getLanguageFromPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/site-content`);
        const contentMap = {};
        Object.entries(res.data).forEach(([key, values]) => {
          contentMap[key] = { key, ...values };
        });
        setContent(contentMap);
      } catch (error) {
        console.error('Failed to load site content:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Get content by key, returns value based on current language
  const getContent = (key, fallback = '') => {
    const item = content[key];
    if (!item) return fallback;
    
    const value = language === 'bg' ? item.value_bg : item.value_en;
    return value || item.value_en || fallback;
  };

  // Shorthand alias
  const t = getContent;

  // Switch language - navigates to the equivalent page in the other language
  const switchLanguage = (newLang) => {
    const currentPath = getPathWithoutLang(location.pathname);
    const newPath = getLocalizedPath(currentPath, newLang);
    navigate(newPath + location.search + location.hash);
  };

  // Get localized link path
  const localizedPath = (path) => {
    return getLocalizedPath(path, language);
  };

  return (
    <SiteContentContext.Provider value={{ 
      content, 
      language, 
      switchLanguage,
      localizedPath,
      getContent, 
      t,
      isLoading 
    }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within a SiteContentProvider');
  }
  return context;
};

// Static translations that don't need to be editable (navigation, buttons, etc.)
export const staticTranslations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      fleet: 'Fleet',
      destinations: 'Destinations',
      whyChooseUs: 'Why Choose Us',
      ldmCalculator: 'Cargo Calculator',
      careers: 'Careers',
      news: 'News',
      faq: 'FAQ',
      contact: 'Contact',
      getQuote: 'Get Quote',
    },
    common: {
      learnMore: 'Learn More',
      readMore: 'Read More',
      applyNow: 'Apply Now',
      submit: 'Submit',
      sending: 'Sending...',
      loading: 'Loading...',
      backToHome: 'Back to Home',
      noResults: 'No results found',
    },
  },
  bg: {
    nav: {
      home: 'Начало',
      about: 'За нас',
      services: 'Услуги',
      fleet: 'Флот',
      destinations: 'Дестинации',
      whyChooseUs: 'Защо нас',
      ldmCalculator: 'Калкулатор',
      careers: 'Кариери',
      news: 'Новини',
      faq: 'Въпроси',
      contact: 'Контакт',
      getQuote: 'Оферта',
    },
    common: {
      learnMore: 'Научи повече',
      readMore: 'Прочети повече',
      applyNow: 'Кандидатствай',
      submit: 'Изпрати',
      sending: 'Изпращане...',
      loading: 'Зареждане...',
      backToHome: 'Към началото',
      noResults: 'Няма резултати',
    },
  },
};

// Hook to get static translation
export const useStaticTranslation = () => {
  const { language } = useSiteContent();
  
  const st = (key) => {
    const keys = key.split('.');
    let value = staticTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };
  
  return { st, language };
};
